import type { ReactNode } from 'react'
import type { Layout } from 'react-grid-layout'
import type { BaseUser, Nil } from '@pbt/pbt-ui-components'
import type { RowData } from '@pbt/pbt-ui-components/src/components/tables/RhapTable'

import type {
  LandingLayoutBreakpoints,
  LandingType,
  WidgetRecordsAssignedMode,
  WidgetWidthType,
} from '~/constants/landingConstants'
import type { Conversation } from '~/types'

import type { Invoice } from './finance'
import type { DiagnoseHistoryItem } from './medicalHistory'
import type { Prescription } from './prescriptions'
import type { Reminder } from './reminders'
import type { ProblemLogEnumReference } from './soap'
import type { Task } from './tasks'
import type { VitalRecord, VitalValue } from './vitals'

export type LandingLayout = Partial<Record<LandingLayoutBreakpoints, Layout[]>>

export type LandingWidget = {
  alias: string
  id: string
}

export type LandingSettingsItem = {
  assignedMode: WidgetRecordsAssignedMode
}

export type LandingSettings = Record<string, LandingSettingsItem>

export type WidgetsData<T = any, K = any> = {
  meta?: K
  page: {
    data: T[]
    totalCount: number
  }
}

export type WidgetsDataParams = {
  landingType: LandingType
  pageSize?: number
  patientId?: string | Nil
  quiet: boolean
}

export type WidgetColumnRow = Record<string, any> & {
  className?: string
  id?: string
}

export type WidgetColumn<T extends WidgetColumnRow = WidgetColumnRow> = {
  allowRowGrowth?: boolean
  clamp?: number
  component?: React.JSXElementConstructor<any>
  disableEllipsis?: boolean
  fixedWidth?: number
  header?: string | ReactNode
  id: string
  isAction?: boolean
  label?: string
  prop?: keyof T | ((item: T & { dateTitle?: string }) => any)
  renderCell?: (rowData: RowData) => string | ReactNode
  width?: number
  widthMap?: Record<WidgetWidthType, number>
  widthTypes?: WidgetWidthType[]
}

export type TasksWidgetColumn = WidgetColumn<Task>

export enum DiagnosisSnapshotItemType {
  DIAGNOSIS_LOG = 'DIAGNOSIS_LOG',
  PROBLEM_DIAGNOSIS_LOG = 'PROBLEM_DIAGNOSIS_LOG',
  PROBLEM_FINDING_LOG = 'PROBLEM_FINDING_LOG',
  DIAGNOSIS_HISTORY = 'DIAGNOSIS_HISTORY',
}

export type DiagnosisSnapshotItem = DiagnoseHistoryItem & {
  businessId: string | Nil
  description: string
  descriptionTranslation: string
  enums?: ProblemLogEnumReference[]
  locationId: string
  problemId?: string
  soapId: string
  statusId?: string
  type?: DiagnosisSnapshotItemType
  vet: BaseUser | null
}

export type MasterProblemsSnapshotItem = DiagnosisSnapshotItem & {
  children: DiagnosisSnapshotItem[]
}

export type AppointmentSnapshotItem = {}

export type CommunicationSnapshotItem = Conversation

export type EstimateSnapshotItem = {}

export type LabSnapshotItem = {
  id: string
  name: string
}

export type PrepaidItemSnapshotItem = {
  id: string
  name: string
}

export type ReminderSnapshotItem = Reminder & {}

export type PrescriptionSnapshotItem = {
  businessId: string | Nil
  controlled: boolean
  date: string
  id: string
  invoice: Pick<Invoice, 'posted' | 'state' | 'id'> | Nil
  invoiceId: string
  isOTC: boolean
  masterName: string
  name: string
  prescription: Prescription
  soapId: string
  state: string
  type: string
}

export type VaccineSnapshotItem = {
  id: string
  name: string
}

export type VitalSnapshotItem = Record<
  keyof VitalValue,
  VitalRecord<number | string>[]
>
